import { createRoot, hydrateRoot } from 'react-dom/client';
import { lazy, Suspense, useEffect, type ErrorInfo } from 'react';

import { ErrorBoundary } from '@/components/error-boundary';
import landingSubdomains from '@/content/landingSubdomains.json';
import { readSiteConsent } from '@/lib/consentStorage';
import './index.css';

const root = document.getElementById('root')!;
const AuthenticatedApp = lazy(() => import('./App'));
const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');
const currentUrl = new URL(window.location.href);
const initialPathname = basePath && currentUrl.pathname.startsWith(basePath)
  ? currentUrl.pathname.slice(basePath.length) || '/'
  : currentUrl.pathname;

if (initialPathname === '/v1/tickets/accept') {
  const ticket = currentUrl.searchParams.get('ticket');
  const signUpUrl = new URL(`${basePath}/sign-up`, window.location.origin);
  if (ticket) signUpUrl.searchParams.set('__clerk_ticket', ticket);
  window.history.replaceState(window.history.state, '', `${signUpUrl.pathname}${signUpUrl.search}`);
}

const pathname = basePath && window.location.pathname.startsWith(basePath)
  ? window.location.pathname.slice(basePath.length) || '/'
  : window.location.pathname;
const isClientOnlyRoute =
  pathname === '/admin' ||
  pathname.startsWith('/admin/') ||
  pathname === '/sign-in' ||
  pathname.startsWith('/sign-in/') ||
  pathname === '/sign-up' ||
  pathname.startsWith('/sign-up/');
const options = {
  // Keeps caught errors off reportError(), which would raise the dev overlay.
  onCaughtError: (error: unknown, errorInfo: ErrorInfo) => {
    console.error(error, errorInfo.componentStack);
  },
};
const authLoadingFallback = (
  <div className="flex min-h-[100dvh] items-center justify-center bg-muted/30 px-4">
    <p className="text-sm text-muted-foreground" role="status">Inlogpagina laden…</p>
  </div>
);
const hasPrerenderedMarkup =
  root.children.length > 0 || Boolean(root.textContent?.trim());

function PublicHydrationReady() {
  useEffect(() => {
    // Loading the route module is not the same as committing its event handlers.
    document.documentElement.dataset.hydrationState = 'ready';
    performance.mark('bodyshape:hydration-ready');
  }, []);
  return null;
}

if (isClientOnlyRoute) {
  createRoot(root, options).render(
    <ErrorBoundary>
      <Suspense fallback={authLoadingFallback}>
        <AuthenticatedApp />
      </Suspense>
    </ErrorBoundary>,
  );
} else {
  let publicAppMounted = false;
  let hydrationPromise: Promise<void> | undefined;
  const startPublicApp = (reason = 'immediate') => hydrationPromise ??= (async () => {
    document.documentElement.dataset.hydrationReason = reason;
    performance.mark('bodyshape:hydration-start');
    if (import.meta.env.DEV && document.documentElement.dataset.copyPreview !== 'true') {
      await import('./lib/developmentRegionalPages');
    }
    const { PublicApp, preparePublicRoute } = await import('./PublicApp');
    if (hasPrerenderedMarkup) await preparePublicRoute(pathname);
    const app = (
      <ErrorBoundary>
        <PublicApp path={pathname} />
        <PublicHydrationReady />
      </ErrorBoundary>
    );
    if (hasPrerenderedMarkup) hydrateRoot(root, app, options);
    else createRoot(root, options).render(app);
    publicAppMounted = true;
  })().catch((error: unknown) => {
    // Never let a failed route download replace a complete SSR document with
    // React's error screen. This applies to customers and crawlers equally.
    document.documentElement.dataset.hydrationState = hasPrerenderedMarkup ? 'server-only' : 'failed';
    console.error('Public hydration unavailable; retaining server-rendered content.', error);
  });

  const isContentFirstRoute =
    pathname === '/prijslijst'
    || pathname.startsWith('/prijslijst/')
    || pathname === '/locatie-kapelle'
    || pathname === '/locatie-vlissingen'
    || pathname === '/fitness-zeeland'
    || pathname === '/skincare-zeeland'
    || pathname === '/gezichtsbehandeling-zeeland'
    || pathname === '/afvallen-zeeland';
  // Campaign CTAs must be interactive immediately even when the short URL is '/'.
  const isCampaignRoot = pathname === '/'
    && Object.hasOwn(landingSubdomains, window.location.hostname.toLowerCase());
  const isStaticFirstRoute = (pathname === '/' && !isCampaignRoot) || pathname === '/behandelingen';

  // Saved permission must take effect on entry, not only after an interaction
  // (or a redundant save in Cookievoorkeuren) starts the consent effects.
  const hasAcceptedConsent = readSiteConsent('statistics') === 'accepted'
    || readSiteConsent('marketing') === 'accepted';

  if (hasAcceptedConsent) {
    void startPublicApp('saved-consent');
  } else if (isStaticFirstRoute && hasPrerenderedMarkup) {
    // These pages are fully usable as prerendered links. Keep their shared
    // navigation/CMS enhancement graph out of the load-critical window and
    // hydrate on the visitor's first interaction instead.
    const startOnIntent = () => void startPublicApp();
    const replayEarlyButtonActivation = (event: MouseEvent) => {
      const target = event.target;
      if (publicAppMounted || !(target instanceof Element)) return;
      const button = target.closest<HTMLButtonElement>('button');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void startPublicApp().then(() => {
        document.removeEventListener('click', replayEarlyButtonActivation, true);
        if (publicAppMounted) window.setTimeout(() => button.click(), 0);
      });
    };
    window.addEventListener('pointerdown', startOnIntent, { once: true });
    window.addEventListener('keydown', startOnIntent, { once: true });
    window.addEventListener('focusin', startOnIntent, { once: true });
    document.addEventListener('click', replayEarlyButtonActivation, true);
    window.addEventListener('pagehide', () => {
      window.removeEventListener('pointerdown', startOnIntent);
      window.removeEventListener('keydown', startOnIntent);
      window.removeEventListener('focusin', startOnIntent);
      document.removeEventListener('click', replayEarlyButtonActivation, true);
    }, { once: true });
  } else if (isContentFirstRoute) {
    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    let idleHandle: number | undefined;
    let fallbackHandle: number | undefined;
    let paintFrame: number | undefined;
    let secondPaintFrame: number | undefined;
    const intentEvents = ['pointerdown', 'keydown', 'focusin'] as const;
    const removeIntentListeners = () => {
      for (const eventName of intentEvents) {
        window.removeEventListener(eventName, startOnIntent);
      }
    };
    const cancelScheduledHydration = () => {
      if (idleHandle !== undefined && idleWindow.cancelIdleCallback) {
        idleWindow.cancelIdleCallback(idleHandle);
      }
      if (fallbackHandle !== undefined) window.clearTimeout(fallbackHandle);
      if (paintFrame !== undefined) window.cancelAnimationFrame(paintFrame);
      if (secondPaintFrame !== undefined) window.cancelAnimationFrame(secondPaintFrame);
    };
    const startOnIntent = () => {
      cancelScheduledHydration();
      removeIntentListeners();
      void startPublicApp('intent');
    };
    const replayEarlyButtonActivation = (event: MouseEvent) => {
      const target = event.target;
      if (publicAppMounted || !(target instanceof Element)) return;
      const button = target.closest<HTMLButtonElement>('button');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cancelScheduledHydration();
      removeIntentListeners();
      void startPublicApp('intent').then(() => {
        document.removeEventListener('click', replayEarlyButtonActivation, true);
        if (publicAppMounted) window.setTimeout(() => button.click(), 0);
      });
    };
    const scheduleAfterFirstPaint = () => {
      paintFrame = window.requestAnimationFrame(() => {
        secondPaintFrame = window.requestAnimationFrame(() => {
          if (idleWindow.requestIdleCallback) {
            idleHandle = idleWindow.requestIdleCallback(
              () => void startPublicApp('idle'),
              { timeout: 750 },
            );
          } else {
            fallbackHandle = window.setTimeout(() => void startPublicApp('post-paint'), 0);
          }
        });
      });
    };
    for (const eventName of intentEvents) {
      window.addEventListener(eventName, startOnIntent, { once: true, passive: true });
    }
    document.addEventListener('click', replayEarlyButtonActivation, true);
    scheduleAfterFirstPaint();
    window.addEventListener('pagehide', () => {
      cancelScheduledHydration();
      removeIntentListeners();
      document.removeEventListener('click', replayEarlyButtonActivation, true);
    }, { once: true });
  } else {
    void startPublicApp();
  }
}
